<script setup lang="ts">
import { onMounted, reactive } from 'vue'
import { NButton, NEmpty, NInput, NPopconfirm, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import StatusPill from '../components/StatusPill.vue'
import { useCalendarCredentialsStore, type CalendarProviderStatus } from '../stores/calendar_credentials'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { NEUTRAL_PILL_CLASS } from '../composables/statusPillColors'

// Admin-only page to manage the calendar heartbeat's OAuth credentials.
// Per provider: set the client id/secret (write-only — the server returns only
// set/not-set), kick off the consent flow, see connection + poll health, and
// disconnect. Auth gating is at the router level; non-admins never reach here.
const store = useCalendarCredentialsStore()
const message = useMessage()
const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()
useDocumentTitle('Calendar credentials')

// The browser OAuth consent route is provider-specific; only Google ships
// today. This map can be extended as providers are added.
const CONNECT_PATHS: Record<string, string> = { google: '/oauth/google_calendar' }

// Per-provider draft inputs (write-only) + in-flight flag, lazily created.
interface Draft {
  clientId: string
  clientSecret: string
  saving: boolean
}
const drafts = reactive<Record<string, Draft>>({})
function draftFor(provider: string): Draft {
  return (drafts[provider] ??= { clientId: '', clientSecret: '', saving: false })
}

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    // loadError surfaces in the template; nothing to do here.
  }
})

async function save(provider: string): Promise<void> {
  const draft = draftFor(provider)
  if (!draft.clientId.trim() && !draft.clientSecret.trim()) {
    message.error('Enter a client ID and/or secret to save')
    return
  }
  draft.saving = true
  try {
    await store.saveClient(provider, draft.clientId.trim(), draft.clientSecret.trim())
    draft.clientId = ''
    draft.clientSecret = ''
    message.success('Credentials saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save credentials')
  } finally {
    draft.saving = false
  }
}

function connect(provider: string): void {
  const path = CONNECT_PATHS[provider]
  if (path) window.open(path, '_blank', 'noopener')
}

async function disconnect(provider: string): Promise<void> {
  try {
    await store.disconnect(provider)
    message.success('Disconnected')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to disconnect')
  }
}

function idPlaceholder(p: CalendarProviderStatus): string {
  return p.client_configured ? 'Client ID is set — enter a new value to replace it' : 'Google OAuth client ID'
}
function secretPlaceholder(p: CalendarProviderStatus): string {
  return p.client_configured ? 'Secret is set — enter a new value to replace it' : 'Google OAuth client secret'
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Calendar credentials</h1>
        <p class="text-sm text-white/60 m-0">
          Manage the OAuth credentials the calendar heartbeat polls with. Set the client ID and secret here (stored
          encrypted — no redeploy needed), connect the account, and check poll health. Secrets are write-only: the page
          only shows whether each value is set, never the value itself.
        </p>
      </header>

      <div v-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="calendar-load-error">
        Failed to load calendar credentials: {{ store.loadError }}
      </div>

      <NEmpty v-else-if="store.providers.length === 0" description="No calendar providers available." class="my-12" />

      <ul v-else class="list-none p-0 m-0 flex flex-col gap-4" data-testid="calendar-provider-list">
        <li
          v-for="p in store.providers"
          :key="p.provider"
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3"
          :data-testid="`calendar-provider-${p.provider}`"
        >
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-lg font-semibold text-white m-0">{{ p.label }}</h2>
            <StatusPill
              :label="p.connected ? 'Connected' : 'Not connected'"
              :color-class="p.connected ? 'bg-emerald-500/15 text-emerald-300/90' : NEUTRAL_PILL_CLASS"
              :data-testid="`calendar-status-${p.provider}`"
            />
          </div>

          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs m-0">
            <dt class="text-white/40">OAuth client</dt>
            <dd class="m-0 text-white/70">{{ p.client_configured ? 'Set' : 'Not set' }}</dd>
            <template v-if="p.scope">
              <dt class="text-white/40">Scope</dt>
              <dd class="m-0 text-white/70 font-mono truncate" :title="p.scope">{{ p.scope }}</dd>
            </template>
            <template v-if="p.access_token_expires_at">
              <dt class="text-white/40">Token expires</dt>
              <dd class="m-0 text-white/70" :title="formatAbsoluteDate(p.access_token_expires_at)">
                {{ formatDate(p.access_token_expires_at, now) }}
              </dd>
            </template>
            <template v-if="p.last_polled_at">
              <dt class="text-white/40">Last successful poll</dt>
              <dd class="m-0 text-white/70" :title="formatAbsoluteDate(p.last_polled_at)">
                {{ formatDate(p.last_polled_at, now) }}
              </dd>
            </template>
          </dl>

          <p
            v-if="p.last_poll_error"
            class="text-amber-300 text-xs border border-amber-400/30 rounded px-2 py-1 m-0"
            :data-testid="`calendar-poll-error-${p.provider}`"
          >
            Last poll failed: {{ p.last_poll_error }}
          </p>

          <div class="flex flex-col gap-2 border-t border-white/10 pt-3">
            <label class="flex flex-col gap-1 text-xs text-white/60">
              Client ID
              <NInput
                v-model:value="draftFor(p.provider).clientId"
                :placeholder="idPlaceholder(p)"
                :disabled="draftFor(p.provider).saving"
                :data-testid="`calendar-client-id-${p.provider}`"
              />
            </label>
            <label class="flex flex-col gap-1 text-xs text-white/60">
              Client secret
              <NInput
                v-model:value="draftFor(p.provider).clientSecret"
                type="password"
                show-password-on="click"
                :placeholder="secretPlaceholder(p)"
                :disabled="draftFor(p.provider).saving"
                :data-testid="`calendar-client-secret-${p.provider}`"
              />
            </label>
            <div class="flex flex-wrap items-center gap-2">
              <NButton
                type="primary"
                size="small"
                :loading="draftFor(p.provider).saving"
                :data-testid="`calendar-save-${p.provider}`"
                @click="save(p.provider)"
                >Save credentials</NButton
              >
              <NButton
                size="small"
                :disabled="!p.client_configured"
                :title="p.client_configured ? '' : 'Set the client ID and secret first'"
                :data-testid="`calendar-connect-${p.provider}`"
                @click="connect(p.provider)"
                >{{ p.connected ? 'Reconnect' : 'Connect' }} →</NButton
              >
              <NButton size="small" tertiary :data-testid="`calendar-refresh-${p.provider}`" @click="store.fetchAll()">
                Refresh status
              </NButton>
              <NPopconfirm
                v-if="p.connected"
                positive-text="Disconnect"
                negative-text="Cancel"
                :positive-button-props="{ type: 'error' }"
                @positive-click="disconnect(p.provider)"
              >
                <template #trigger>
                  <NButton size="small" tertiary type="error" :data-testid="`calendar-disconnect-${p.provider}`">
                    Disconnect
                  </NButton>
                </template>
                Disconnect {{ p.label }}? This clears the stored tokens; the heartbeat stops until you reconnect. Your
                client ID/secret stay configured.
              </NPopconfirm>
            </div>
            <p class="text-xs text-white/40 m-0">
              Connect opens Google's consent screen in a new tab. After granting access, return here and hit “Refresh
              status”.
            </p>
          </div>
        </li>
      </ul>
    </section>
  </RouteShell>
</template>
